import { ApolloError, SingleExecutionResult } from '@apollo/client';
import { ErrorPage, LoadingPage, Modal, ModalForm, TControlledFormProps } from '@chocolate-soup-inc/cs-frontend-components';
import { joiResolver } from '@hookform/resolvers/joi';
import { useCallback, useEffect, useMemo } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getUserFormFields } from '../../entities/users/formFields';
import { getUserSchema } from '../../entities/users/schema';
import { TCreateUserMutation, TCreateUserMutationInput, useCreateUserMutation, useGetCompanyLazyQuery } from '../../generated/graphql';

export const CompanyUsersInvite = () => {
  const navigate = useNavigate();
  const { companyId = '0' } = useParams();

  const onBackClick = useCallback(() => {
    navigate(generatePath('../'));
  }, [navigate]);

  const closeModal = useCallback(() => {
    navigate(generatePath('../../'));
  }, [navigate]);

  const [getCompany, { data: getCompanyData, loading: getCompanyLoading, error: getCompanyError }] = useGetCompanyLazyQuery();

  useEffect(() => {
    getCompany({
      variables: {
        id: companyId
      }
    });
  }, [companyId, getCompany]);

  const [createUser] = useCreateUserMutation();

  const onSuccess = useCallback(
    (data: SingleExecutionResult<TCreateUserMutation>) => {
      if (data.errors) data.errors.forEach((err) => toast.error(err.message));
      else {
        toast.success('Successfully invited the user.');
        onBackClick();
      }
    },
    [onBackClick]
  );

  const onSubmitError = useCallback((error: ApolloError) => {
    toast.error('There was an error creating the company.');
    if (error instanceof ApolloError) error.graphQLErrors.forEach((err) => toast.error(err.message));
  }, []);

  const onSubmit = useCallback<TControlledFormProps<TCreateUserMutationInput>['onValidSubmit']>(
    (formData) => {
      return createUser({
        variables: {
          input: {
            ...formData,
            companyId
          }
        }
      })
        .then(onSuccess)
        .catch(onSubmitError);
    },
    [companyId, createUser, onSubmitError, onSuccess]
  );

  const userSchema = useMemo(() => {
    if (!getCompanyData?.getCompany) return;
    return getUserSchema({ company: getCompanyData.getCompany });
  }, [getCompanyData]);

  if (getCompanyLoading)
    return (
      <Modal headline='Invite User'>
        <LoadingPage />
      </Modal>
    );

  if (getCompanyError) {
    console.error('query Apollo ERROR:', getCompanyError);
    toast.error('An error has ocurred, check the console for details. (query Apollo ERROR)');
    //if (getCompanyError.message) toast.error(getCompanyError.message);
    if (!getCompanyData)
      return (
        <Modal headline='Invite User'>
          <ErrorPage error={getCompanyError} />
        </Modal>
      );
  }

  if (!userSchema)
    //toast.error('Company not found.');
    return (
      <Modal headline='Invite User'>
        <ErrorPage error={{ name: 'noCompany', message: '[Company] User schema not found.' }} />
      </Modal>
    );

  return (
    <ModalForm<TCreateUserMutationInput>
      onBackClick={onBackClick}
      closeModal={closeModal}
      headline='Invite User'
      controlledFormProps={{
        fields: getUserFormFields(),
        formProps: {
          resolver: joiResolver(userSchema, {
            convert: true,
            abortEarly: false,
            stripUnknown: false
          })
        },
        onValidSubmit: onSubmit,
        onInvalidSubmit: (errors) => console.error(errors)
      }}
    />
  );
};
